import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, ChevronDown, FileText } from 'lucide-react';
import Banner from '../common/Banner';
import PrimaryButton from '../common/PrimaryButton';
import { DisciplinePill, LoadingSpinner } from '../common';
import { ClaimsService, blockText } from '../../services/billing/claims.service';
import { fetchPatients } from '../../services/admin/patient.service';
import type { BillingReadiness, PayerGroup, ReadinessVisit } from '../../dtos/billing/claims.dto';

/** Phase 20F: claim flags (never blocks) */
const FLAG_TEXT: Record<string, string> = {
  EVV_OVERRIDE: 'EVV override — flagged on the claim',
  THERAPY_LINE_UNDER_ONE_UNIT: 'A therapy procedure under 15 minutes was left off the claim',
  HOSPICE_ELECTED: 'Hospice elected: check the care is not the hospice’s responsibility',
  PPECC_ATTENDANCE_DAY: 'PDN on a PPECC attendance day',
};
export const flagText = (code: string): string => FLAG_TEXT[code] || (code.endsWith('OVERRIDE') ? 'EVV override — flagged on the claim' : code);

interface ClaimsProps {
  expanded?: boolean;
  toggleExpanded?: () => void;
  patientId?: string;
  onGoToReconciliation?: () => void;
  /** Open a generated claim in the register */
  onOpenClaim?: (claimId: string) => void;
}

interface PatientOption {
  id: string;
  name: string;
  mrn: string;
}

const AGING_CLASS: Record<string, string> = {
  ok: 'text-slate-600',
  amber: 'font-medium text-amber-700',
  red: 'font-semibold text-red-700',
};

const groupKey = (g: PayerGroup) => g.payer?.coverageId || 'NONE';

/**
 * Generate claims (Phase 11). Everything on this screen comes from the server readiness source
 * (GET /billing/claims/visits-ready, REQ-BIL-01..05): coverage and reconciliation stops first,
 * then each visit with its units (HHAX minutes for EVV-required visits), charge, days since
 * service and the reason it cannot be billed. Generate builds CMS-1500 claims of up to 6 lines.
 */
const Claims: React.FC<ClaimsProps> = ({ expanded = true, toggleExpanded, patientId, onGoToReconciliation, onOpenClaim }) => {
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState(patientId || '');
  const [readiness, setReadiness] = useState<BillingReadiness | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedVisits, setSelectedVisits] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorItems, setErrorItems] = useState<string[]>([]);
  const [generated, setGenerated] = useState<{ count: number; firstClaimId: string; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPatients(1, 100)
      .then((result) => {
        if (cancelled) return;
        setPatients(result.patients.map((p) => ({ id: p.id, name: `${p.firstName} ${p.lastName}`, mrn: p.mrn || '' })));
      })
      .catch(() => { if (!cancelled) setError('Failed to load patients. Please try again.'); });
    return () => { cancelled = true; };
  }, []);

  const loadReadiness = useCallback(async (id: string) => {
    if (!id) { setReadiness(null); return; }
    setLoading(true);
    setError(null);
    setErrorItems([]);
    try {
      const result = await ClaimsService.getReadiness(id);
      setReadiness(result);
      const firstReady = result.payers.find((g) => g.readyCount > 0) || result.payers[0];
      setSelectedGroup(firstReady ? groupKey(firstReady) : '');
    } catch (err) {
      setReadiness(null);
      setError(err instanceof Error ? err.message : 'Failed to load billing readiness');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedVisits([]);
    setGenerated(null);
    loadReadiness(selectedPatientId);
  }, [selectedPatientId, loadReadiness]);

  const group = readiness?.payers.find((g) => groupKey(g) === selectedGroup) || null;
  const groupVisits: ReadinessVisit[] = useMemo(
    () => (readiness && group ? readiness.visits.filter((v) => group.visitIds.includes(v.visitId)) : []),
    [readiness, group]
  );
  const readyVisits = groupVisits.filter((v) => v.ready);
  const selectedRows = groupVisits.filter((v) => selectedVisits.includes(v.visitId));
  const selectedTotal = selectedRows.reduce((sum, v) => sum + (v.charge || 0), 0);
  const claimCount = Math.ceil(selectedRows.length / 6);

  const toggle = (visitId: string) => {
    setSelectedVisits((prev) => (prev.includes(visitId) ? prev.filter((id) => id !== visitId) : [...prev, visitId]));
  };

  const handleGenerate = async () => {
    if (selectedVisits.length === 0) return;
    setGenerating(true);
    setError(null);
    setErrorItems([]);
    setGenerated(null);
    try {
      const result = await ClaimsService.generateClaims(selectedVisits);
      setGenerated({
        count: result.claims.length,
        firstClaimId: result.claims[0]?.claimId,
        total: result.claims.reduce((sum, c) => sum + (c.totalAmount || 0), 0),
      });
      setSelectedVisits([]);
      await loadReadiness(selectedPatientId);
    } catch (err) {
      const parsed = ClaimsService.parseGenerationError(err);
      setError(parsed.message);
      if (parsed.kind === 'notReady') setErrorItems(parsed.missing);
      if (parsed.kind === 'alreadyBilled') await loadReadiness(selectedPatientId);
    } finally {
      setGenerating(false);
    }
  };

  const header = (
    <h2 className="flex items-center text-lg font-medium">
      <FileText size={20} className="mr-2" />
      Generate Claims
    </h2>
  );

  return (
    <div className="mb-0">
      <div className="overflow-hidden rounded bg-white shadow">
        {toggleExpanded ? (
          <div className="flex cursor-pointer items-center justify-between border-b border-gray-100 p-4" onClick={toggleExpanded}>
            {header}
            <ChevronDown size={20} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        ) : (
          <div className="border-b border-gray-100 p-4">{header}</div>
        )}

        {expanded && (
          <div className="space-y-4 p-4 text-left sm:p-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label htmlFor="claims-patient" className="mb-1 block text-sm font-medium text-gray-700">Patient</label>
                <select
                  id="claims-patient"
                  value={selectedPatientId}
                  onChange={(e) => setSelectedPatientId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                >
                  <option value="">Choose a patient...</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.name}{p.mrn ? ` (${p.mrn})` : ''}</option>)}
                </select>
              </div>
              {readiness && readiness.payers.length > 0 && (
                <div>
                  <label htmlFor="claims-payer" className="mb-1 block text-sm font-medium text-gray-700">Payer (primary on the date of service)</label>
                  <select
                    id="claims-payer"
                    value={selectedGroup}
                    onChange={(e) => { setSelectedGroup(e.target.value); setSelectedVisits([]); }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  >
                    {readiness.payers.map((g) => (
                      <option key={groupKey(g)} value={groupKey(g)}>
                        {g.payer ? g.payer.payerName : 'No active primary coverage'} — {g.readyCount} of {g.visitIds.length} ready
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {loading && <LoadingSpinner className="justify-start" />}

            {readiness && readiness.reconciliationGaps.length > 0 && (
              <Banner
                variant="error"
                title="Reconciliation required before billing"
                data-testid="reconciliation-stop"
                action={onGoToReconciliation && (
                  <button type="button" onClick={onGoToReconciliation} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white">Go to Reconciliation</button>
                )}
              >
                No HHAX reconciliation covers these EVV dates of service. Upload the HHAX export for:
                <span className="mt-1 block font-medium">{readiness.reconciliationGaps.map(ClaimsService.formatDate).join(', ')}</span>
              </Banner>
            )}
            {readiness && readiness.noPrimaryDates.length > 0 && (
              <Banner variant="error" title="No active primary coverage" data-testid="no-primary-stop">
                Visits on these dates have no active primary coverage and cannot be billed: {readiness.noPrimaryDates.map(ClaimsService.formatDate).join(', ')}
              </Banner>
            )}
            {readiness && [...readiness.agencyBlocks, ...readiness.patientBlocks].map((b) => (
              <Banner key={b.code} variant="warning" title={b.scope === 'agency' ? 'Agency setup' : 'Patient record'}>
                {blockText(b)}
              </Banner>
            ))}

            {readiness && readiness.visits.length === 0 && !loading && (
              <p className="text-sm text-slate-500">No completed, unbilled visits for this patient.</p>
            )}

            {group && groupVisits.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="min-w-full text-sm" aria-label="Visits to bill">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-2">
                        <input
                          type="checkbox"
                          aria-label="Select all ready visits"
                          checked={readyVisits.length > 0 && readyVisits.every((v) => selectedVisits.includes(v.visitId))}
                          onChange={(e) => setSelectedVisits(e.target.checked ? readyVisits.map((v) => v.visitId) : [])}
                          disabled={readyVisits.length === 0}
                        />
                      </th>
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">Visit</th>
                      <th className="px-2 py-2 text-left">Minutes</th>
                      <th className="px-2 py-2 text-left">Code</th>
                      <th className="px-2 py-2 text-right">Units</th>
                      <th className="px-2 py-2 text-right">Charge</th>
                      <th className="px-2 py-2 text-right">Days since service</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupVisits.map((v) => {
                      const visitBlocks = v.blocks.filter((b) => b.scope === 'visit');
                      return (
                        <tr key={v.visitId} className={`border-t border-slate-100 align-top ${v.ready ? '' : 'bg-slate-50'}`} data-testid={`visit-row-${v.visitId}`}>
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              aria-label={`Select visit ${ClaimsService.formatDate(v.visitDate)}`}
                              checked={selectedVisits.includes(v.visitId)}
                              onChange={() => toggle(v.visitId)}
                              disabled={!v.ready}
                            />
                          </td>
                          <td className="whitespace-nowrap px-2 py-2">{ClaimsService.formatDate(v.visitDate)}</td>
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-2">
                              {v.discipline && <DisciplinePill discipline={v.discipline} />}
                              <span className="text-xs text-slate-600">{v.providerName || ''}</span>
                            </div>
                            {visitBlocks.length > 0 && (
                              <ul className="mt-1 space-y-0.5 text-xs text-red-700" data-testid={`visit-blocks-${v.visitId}`}>
                                {visitBlocks.map((b) => <li key={b.code}>{blockText(b)}</li>)}
                              </ul>
                            )}
                            {v.flags.length > 0 && (
                              <ul className="mt-1 space-y-0.5 text-xs text-amber-700" data-testid={`visit-flags-${v.visitId}`}>
                                {[...new Set(v.flags.map((f) => f.code))].map((c) => <li key={c}>{flagText(c)}</li>)}
                              </ul>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2">
                            {v.minutes ?? '—'} <span className="text-xs text-slate-500">{v.minutesSource === 'EVV' ? 'HHAX' : 'Pelora'}</span>
                          </td>
                          <td className="whitespace-nowrap px-2 py-2">{v.code ? [v.code.hcpcs, ...v.code.modifiers].join(' ') : '—'}</td>
                          <td className="px-2 py-2 text-right">{v.units ?? '—'}</td>
                          <td className="px-2 py-2 text-right">{v.charge != null ? ClaimsService.formatCurrency(v.charge) : '—'}</td>
                          <td className={`px-2 py-2 text-right ${AGING_CLASS[v.agingLevel]}`} data-testid={`aging-${v.visitId}`}>{v.daysSinceService ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {group && readyVisits.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                <span>
                  {selectedRows.length} selected · {ClaimsService.formatCurrency(selectedTotal)}
                  {claimCount > 1 && <span className="text-slate-500"> · {claimCount} claims (6 lines per claim)</span>}
                </span>
                <PrimaryButton onClick={handleGenerate} disabled={selectedRows.length === 0 || generating} className="bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700 disabled:bg-slate-400">
                  {generating ? 'Generating…' : `Generate claim${claimCount > 1 ? 's' : ''}`}
                </PrimaryButton>
              </div>
            )}

            {error && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <p>{error}</p>
                {errorItems.length > 0 && (
                  <ul className="mt-1 list-disc pl-5" aria-label="Missing claim information">
                    {errorItems.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                )}
              </div>
            )}

            {generated && (
              <div role="status" className="flex flex-wrap items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <CheckCircle size={16} />
                <span>
                  {generated.count > 1 ? `${generated.count} claims generated` : 'Claim generated'} · {ClaimsService.formatCurrency(generated.total)}
                </span>
                {onOpenClaim && generated.firstClaimId && (
                  <button type="button" onClick={() => onOpenClaim(generated.firstClaimId)} className="ml-auto text-sm font-medium text-green-900 underline">Open in register</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Claims;
